import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react'
import Sidebar from './sidebar'
import AppliedFilters from './applied_filters'
import SortBy from './sort_by'
import Pagination from './pagination'
import BackToTop from './back_to_top'
import {
  getForCategory, getCategoryPath, getSearchQuery, getCurrentSelectedFilters,
  getLocationPathname, getProductLinkType, passDataToBackbone
} from './search_page_utils'
/* eslint no-unused-vars: 0 */
import regeneratorRuntime from 'regenerator-runtime'
import PropTypes from 'prop-types'
import { storefrontFetch } from '../misc/fetch_helpers'
import FilterUtils from './filter_utils'
import SearchHeader from './search_header'

export const getRequestParams = (pageNumber, sortBy, selectedFilters, forCategory, categoryPath, searchQuery) => {
  let requestParams = { page: pageNumber, sort_by: sortBy, filters: selectedFilters, q: searchQuery }
  const decodedCategoryPath = decodeURI(categoryPath)
  if (forCategory) {
    requestParams = { ...requestParams, for_category: forCategory }
  }
  if (decodedCategoryPath) {
    requestParams = { ...requestParams, category_path: decodedCategoryPath }
  }

  return requestParams
}

const canShowFocalLengthSortBy = (selectedFilters, categoryPath) => {
  if (categoryPath && categoryPath.indexOf('lenses') !== -1) {
    return true
  }
  if (selectedFilters) {
    const currentSelectedFilters = JSON.parse(selectedFilters)
    if (currentSelectedFilters.string_filters) {
      const itemTypeFiler = currentSelectedFilters.string_filters.find(filter => filter.name === 'Item Type')
      if (itemTypeFiler && itemTypeFiler.values.some(value => value.indexOf('Lens') !== -1)) {
        return true
      }
    }
  }
  return false
}
const showingOnlyForSaleProducts = (selectedFilters) => {
  if (selectedFilters) {
    const currentSelectedFilters = JSON.parse(selectedFilters)
    if (currentSelectedFilters.string_filters) {
      const forSaleFilter = currentSelectedFilters.string_filters.find(filter => filter.name === 'For Sale')
      if (forSaleFilter && forSaleFilter.values.some(value => value.indexOf('Yes') !== -1)) {
        return true
      }
    }
  }
  return false
}

export const useSearchApi = () => {
  const [data, setData] = useState()
  const defaultSelectedFilters = JSON.stringify({
    string_filters: [],
    number_filters: [],
    number_range_filters: []
  })
  const [selectedFilters, setSelectedFilters] = useState(defaultSelectedFilters)
  const [sortBy, setSortBy] = useState('heat')
  const [pageNumber, setPageNumber] = useState(1)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showFocalLengthSortBy, setShowFocalLengthSortBy] = useState(canShowFocalLengthSortBy(selectedFilters, getCategoryPath()))
  // TODO will be used once header search box is a React component, so we could update the searchTerm
  // const [searchTerm, setSearchTerm] = useState('');

  const firstUpdate = useRef(true)
  const secondUpdate = useRef(false)
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false

      const buyUrl = getLocationPathname().split('/')[1] === 'buy'
      let currentSelectedFilters = getCurrentSelectedFilters().replace(/&quot;/g, '"')

      if (currentSelectedFilters === '' || currentSelectedFilters === '{}') {
        currentSelectedFilters = JSON.stringify({
          string_filters: buyUrl ? [{ name: 'For Sale', values: ['Yes'] }] : [],
          number_filters: [],
          number_range_filters: []
        })
      // if !buyUrl, which means the user is on a page with /rent/, we don't want to set secondUpdate
      } else if (!buyUrl && currentSelectedFilters !== defaultSelectedFilters) {
        // Optimization: skip calling fetchData when useEffect is called second time as result of setting
        // currentSelectedFilters that are not empty and are different than default filters
        secondUpdate.current = true
      }
      setSelectedFilters(currentSelectedFilters)
      return
    }
    if (!firstUpdate.current && secondUpdate.current) {
      secondUpdate.current = false
      return
    }
    const fetchData = async () => {
      setIsError(false)
      setIsLoading(true)
      const forCategory = getForCategory()
      const productLinkType = getProductLinkType() || 'rent'
      let ajaxUrl

      if (forCategory) {
        if (getLocationPathname().indexOf('catalog_search') === -1) {
          ajaxUrl = `/${productLinkType}/${getCategoryPath()}.json`
        } else {
          ajaxUrl = '/catalog_search.json'
        }
      } else {
        ajaxUrl = '/catalog_search.json'
      }
      const requestParams = getRequestParams(pageNumber, sortBy, selectedFilters, forCategory, getCategoryPath(), getSearchQuery())

      try {
        const result = await storefrontFetch(ajaxUrl, {
          method: 'GET'
        }, requestParams)

        // noinspection JSCheckFunctionSignatures
        setData(result)
        setShowFocalLengthSortBy(canShowFocalLengthSortBy(selectedFilters, getCategoryPath()))

        const str = Object.entries(requestParams).map(([key, val]) => `${key}=${val}`).join('&')
        history.pushState(requestParams, 'search page', `${ajaxUrl.replace('.json', '')}?${str}`)

        passDataToBackbone(result, showingOnlyForSaleProducts(selectedFilters))
      } catch (error) {
        setIsError(true)
      }
      setIsLoading(false)
    }
    fetchData()
  }, [pageNumber, sortBy, selectedFilters, defaultSelectedFilters])

  return [{
    data,
    isError,
    isLoading,
    selectedFilters,
    sortBy,
    showFocalLengthSortBy,
    pageNumber
  }, setPageNumber, setSortBy, setSelectedFilters]
}

export const scrollToTop = (event) => {
  if (event) event.preventDefault()
  window.scrollTo({ top: 100, behavior: 'smooth' })
}

const SearchPage = props => {
  const { filters, initialSearchResults } = props

  const [{ data, isError, isLoading, selectedFilters, sortBy, showFocalLengthSortBy, pageNumber },
    setPageNumber, setSortBy, setSelectedFilters] = useSearchApi()

  const handleFilterSelection = useCallback((newSelectedFilters) => {
    setSelectedFilters(newSelectedFilters)
    setPageNumber(1)
  }, [setSelectedFilters, setPageNumber])

  let searchResults
  let currentPage
  if (data === undefined) {
    searchResults = initialSearchResults
    currentPage = initialSearchResults.current_page
  } else {
    searchResults = data
    currentPage = pageNumber
  }
  let totalRecords = searchResults ? searchResults.total_records : 0
  let totalPages = searchResults ? searchResults.total_pages : 0
  // noinspection JSUnresolvedVariable
  const inStockTotalRecords = searchResults ? searchResults.in_stock_total_records : 0
  if (inStockTotalRecords === 0) {
    totalRecords = 0
    totalPages = 0
  }

  const recordsPerPage = searchResults ? searchResults.records_per_page : 20
  return (
    <>
      <SearchHeader isLoading={isLoading} isError={isError} />
      <Sidebar
        filters={filters}
        data={data}
        selectedFilters={selectedFilters}
        handleFilterSelection={handleFilterSelection}
      />
      <AppliedFilters
        selectedFilters={selectedFilters}
        handleFilterSelection={handleFilterSelection}
        deselectFilter={FilterUtils().deselectFilter}
        deselectMinMaxFilter={FilterUtils().deselectMinMaxFilter}
        deselectAtLeastFilter={FilterUtils().deselectAtLeastFilter}
      />
      {/* TODO: when search input box is react component, replace condition with: (!searchTerm || searchTerm.length === 0) */}
      {(!getSearchQuery() || getSearchQuery().length === 0) &&
        <SortBy
          sortBy={sortBy}
          setSortBy={setSortBy}
          showFocalLengthSortBy={showFocalLengthSortBy}
          selectedFilters={selectedFilters}
          isLoading={isLoading}
        />}

      <Pagination
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        recordsPerPage={recordsPerPage}
        setPageNumber={setPageNumber}
        mountPoint={document.getElementById('pagination')}
      />
      <BackToTop
        mountPoint={document.getElementById('back-to-top')}
      />
    </>
  )
}
SearchPage.propTypes = {
  filters: PropTypes.object.isRequired,
  initialSearchResults: PropTypes.object.isRequired
}

export default SearchPage
